<script setup lang="ts">
  import { ref, computed } from "vue";
  import { Input } from "@/components/ui/input";
  import { Badge } from "@/components/ui/badge";
  import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
  import { X, Loader2 } from "lucide-vue-next";
  import { useProspectSearch, suggestionLabel, type Prospect } from "@/utils/composables/useProspectSearch";

  export type { Prospect };

  const props = withDefaults(defineProps<{ modelValue?: Prospect[] }>(), {
    modelValue: () => [],
  });

  const emit = defineEmits<{ "update:modelValue": [value: Prospect[]] }>();

  const prospects = computed(() => props.modelValue);
  const { emailInput, loading, searching, showSuggestions, suggestions, errorMessage, selectSuggestion, addTypedEmail } =
    useProspectSearch(prospects, (prospect) => emit("update:modelValue", [...props.modelValue, prospect]));

  const removeProspect = (index: number): void => {
    emit(
      "update:modelValue",
      props.modelValue.filter((_, i) => i !== index),
    );
  };

  const updateProspect = (index: number, patch: Partial<Prospect>): void => {
    emit(
      "update:modelValue",
      props.modelValue.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    );
  };

  const anchorRef = ref<HTMLElement | null>(null);

  // Keep the popover open while the user clicks/types back into the input.
  const onPointerDownOutside = (event: {
    detail?: { originalEvent?: Event };
    preventDefault: () => void;
  }): void => {
    const target = event.detail?.originalEvent?.target as Node | undefined;
    if (target && anchorRef.value?.contains(target)) event.preventDefault();
  };

  const onPopoverOpenChange = (value: boolean): void => {
    if (!value) showSuggestions.value = false;
  };
</script>

<template>
  <div class="grid gap-3">
    <Popover
      :open="showSuggestions"
      @update:open="onPopoverOpenChange">
      <PopoverAnchor as-child>
        <div
          ref="anchorRef"
          class="flex gap-2">
          <Input
            v-model="emailInput"
            type="text"
            placeholder="Search a user or type an email…"
            class="text-sm"
            :disabled="loading"
            autocomplete="off"
            @keydown.enter.prevent="addTypedEmail"
            @keydown.esc="showSuggestions = false" />
          <button
            type="button"
            class="border-input hover:bg-muted inline-flex items-center rounded-md border px-3 text-sm disabled:opacity-50"
            :disabled="loading || !emailInput.trim()"
            @click="addTypedEmail">
            <Loader2
              v-if="loading"
              class="size-4 animate-spin" />
            <span v-else>Add</span>
          </button>
        </div>
      </PopoverAnchor>

      <PopoverContent
        side="bottom"
        align="start"
        class="max-h-64 w-(--reka-popover-trigger-width) overflow-y-auto p-0"
        @open-auto-focus.prevent
        @close-auto-focus.prevent
        @pointer-down-outside="onPointerDownOutside">
        <div
          v-if="searching"
          class="text-muted-foreground flex items-center gap-2 px-3 py-2 text-sm">
          <Loader2 class="size-4 animate-spin" />
          Searching…
        </div>
        <ul v-else-if="suggestions.length">
          <li
            v-for="user in suggestions"
            :key="user.id">
            <button
              type="button"
              class="hover:bg-accent flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left"
              @click="selectSuggestion(user)">
              <span class="text-sm font-medium">{{ suggestionLabel(user) || user.email }}</span>
              <span class="text-muted-foreground text-xs">{{ user.email }}</span>
            </button>
          </li>
        </ul>
        <div
          v-else
          class="text-muted-foreground px-3 py-2 text-sm">
          No user found — press Enter to invite a new one
        </div>
      </PopoverContent>
    </Popover>

    <p
      v-if="errorMessage"
      class="text-destructive text-xs">
      {{ errorMessage }}
    </p>

    <ul
      v-if="props.modelValue.length"
      class="grid gap-2">
      <li
        v-for="(prospect, index) in props.modelValue"
        :key="prospect.email"
        class="rounded-lg border p-3">
        <div class="flex items-center justify-between gap-2">
          <div class="flex min-w-0 items-center gap-2">
            <Badge
              :variant="prospect.status === 'existing' ? 'secondary' : 'default'"
              class="text-xs">
              {{ prospect.status === "existing" ? "Existing" : "New" }}
            </Badge>
            <span class="truncate text-sm font-medium">{{ prospect.email }}</span>
          </div>
          <button
            type="button"
            class="hover:bg-muted rounded-full p-1"
            @click="removeProspect(index)">
            <X class="size-4" />
          </button>
        </div>

        <div
          v-if="prospect.status === 'existing'"
          class="text-muted-foreground mt-1 text-xs">
          {{ prospect.fullName || "—" }}
          <template v-if="prospect.accounts?.length"> · {{ prospect.accounts.map((a) => a.name).join(", ") }} </template>
        </div>

        <div
          v-else
          class="mt-2 grid grid-cols-2 gap-2">
          <Input
            :model-value="prospect.first_name"
            placeholder="First name"
            class="text-sm"
            @update:model-value="updateProspect(index, { first_name: String($event) })" />
          <Input
            :model-value="prospect.last_name"
            placeholder="Last name"
            class="text-sm"
            @update:model-value="updateProspect(index, { last_name: String($event) })" />
        </div>
      </li>
    </ul>
  </div>
</template>
